import React, { useState, useEffect } from "react";
import DashboardLayout from "./DashboardLayout";
import institutionData from "../data/eligibility-data/institution_data.json";
import subjectsData from "../data/eligibility-data/subjects.json";
import jambSubjectsData from "../data/eligibility-data/subjects_jamb.json";
import EligibilityModal from "../components/modals/EligibilityModal.tsx";
import {
  getEligibilityCheckerProgram,
  submitEligibilityChecker,
} from "../api/services.js";
import EligibilityForm from "../components/eligibility-checker/eligibility-form.tsx";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const options = (items) =>
  items.map((item) => ({
    label: item.title || item.name || item,
    value: item.itemid || item.id || item,
  }));

export default function EligibilityChecker() {
  const [formData, setFormData] = useState({
    entryMode: "",
    institutionType: "",
    institutionTypeId: null,
    institutionCategory: "",
    institutionCategoryId: null,
    institution: "",
    institutionId: null,
    program: "",
    programId: null,
    olevelCredits: [],
    olevelPasses: [],
    alevelCredits: [],
    alevelPasses: [],
    utmeSubjects: [{ label: "English Language", value: "English", id: 18 }],
  });

  const [showModal, setShowModal] = useState(false);
  const [institutionTypes, setInstitutionTypes] = useState([]);
  const [institutionCategories, setInstitutionCategories] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [eligibilityData, setEligibilityData] = useState(null);

  const olevelSubjects = options(subjectsData);
  const alevelSubjects = options(subjectsData);
  const utmeSubjects = options(jambSubjectsData);

  useEffect(() => {
    const types = institutionData
      .filter((type) => type.title !== "NID")
      .map((type) => ({
        label: type.title,
        value: type.id,
        children: type.children,
      }));

    setInstitutionTypes(types);
  }, []);

  const handleInstitutionTypeChange = (selectedType) => {
    setInstitutionCategories([]);
    setInstitutions([]);
    setPrograms([]);

    setFormData((prev) => ({
      ...prev,
      institutionType: selectedType?.label || "",
      institutionTypeId: selectedType?.value || null,
      institutionCategory: "",
      institutionCategoryId: null,
      institution: "",
      institutionId: null,
      program: "",
      programId: null,
    }));

    if (!selectedType) return;

    const categories = selectedType.children.map((category) => ({
      label: category.title,
      value: category.id,
      children: category.children,
    }));

    setInstitutionCategories(categories);
  };

  const handleInstitutionCategoryChange = (selectedCategory) => {
    if (!selectedCategory) {
      setInstitutions([]);
      setPrograms([]);
      setFormData((prev) => ({
        ...prev,
        institutionCategory: "",
        institutionCategoryId: null,
        institution: "",
        institutionId: null,
        program: "",
        programId: null,
      }));
      return;
    }

    const institutionsList = selectedCategory.children.map((inst) => ({
      label: inst.name,
      value: inst.id,
    }));

    setInstitutions(institutionsList);
    setPrograms([]);
    setFormData((prev) => ({
      ...prev,
      institutionCategory: selectedCategory.label,
      institutionCategoryId: selectedCategory.value,
      institution: "",
      institutionId: null,
      program: "",
      programId: null,
    }));
  };
  const handleChange = async (field, value) => {
    if (field === "institution") {
      if (!value) {
        setPrograms([]);
        setFormData((prev) => ({
          ...prev,
          institution: "",
          institutionId: null,
          program: "",
          programId: null,
        }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        institution: value.label,
        institutionId: value.value,
        program: "",
        programId: null,
      }));

      setLoadingPrograms(true);
      try {
        const eligibilityData = await getEligibilityCheckerProgram(value.value);
        const programmeOptions = (eligibilityData || []).map((p) => ({
          label: p.name || p.title,
          value: p.id,
        }));
        setPrograms(programmeOptions);
      } catch (err) {
        console.error("Error loading programs:", err);
        setPrograms([]);
        toast.error(
          err.message || "Failed to load programs. Please try again."
        );
      } finally {
        setLoadingPrograms(false);
      }
    } else if (field === "program" && value) {
      setFormData((prev) => ({
        ...prev,
        program: value.label,
        programId: value.value,
      }));
    } else if (field === "program" && !value) {
      setFormData((prev) => ({
        ...prev,
        program: "",
        programId: null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value?.label || value,
      }));
    }
  };

  const handleSubjectSelection = (field, selectedOptions) => {
    if (field === "utmeSubjects" && selectedOptions) {
      const englishSubject = utmeSubjects.find(
        (subj) => subj.label === "English Language"
      );
      const hasEnglish = selectedOptions.some(
        (subj) => subj.label === "English Language"
      );

      if (!hasEnglish && englishSubject) {
        selectedOptions = [englishSubject, ...selectedOptions];
      }

      if (selectedOptions.length > 4) {
        selectedOptions = selectedOptions.slice(0, 4);
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field]: selectedOptions || [],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      mode_of_entry: formData.entryMode === "Direct Entry" ? "DE" : "UTME",
      institution: formData.institutionId,
      select_programme: formData.programId,
      institution_category: formData.institutionCategoryId,
      institution_type: formData.institutionTypeId,
      olevel_credit: formData.olevelCredits.map((s) => s.label || s),
      olevel_passes: formData.olevelPasses.map((s) => s.label || s),
      alevel_credit: formData.alevelCredits?.map((s) => s.label || s) || [],
      alevel_passes: formData.alevelPasses?.map((s) => s.label || s) || [],
      utme_subjects: formData.utmeSubjects.map((s) => s.label || s),
    };

    try {
      const result = await submitEligibilityChecker(payload);

      setEligibilityData(result);

      setShowModal(true);
    } catch (err) {
      const apiError = err?.response?.data || err;
      const validationErrors = apiError?.errors
        ? Object.values(apiError.errors).flat()
        : [];

      if (validationErrors.length > 0) {
        validationErrors.forEach((msg) => toast.error(msg));
      } else {
        toast.error(
          apiError?.message ||
            apiError?.status ||
            "Submission failed. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout
      brochure=" Integrated Brochure & Syllabus System (IBASS)"
      childTab={11}
      animated={true}
    >
      <EligibilityForm
        formData={formData}
        institutionTypes={institutionTypes}
        institutionCategories={institutionCategories}
        institutions={institutions}
        programs={programs}
        loadingPrograms={loadingPrograms}
        olevelSubjects={olevelSubjects}
        alevelSubjects={alevelSubjects}
        utmeSubjects={utmeSubjects}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        handleInstitutionTypeChange={handleInstitutionTypeChange}
        handleInstitutionCategoryChange={handleInstitutionCategoryChange}
        handleSubjectSelection={handleSubjectSelection}
        loading={loading}
      />

      {showModal && eligibilityData && (
        <EligibilityModal setModal={setShowModal} data={eligibilityData} />
      )}

      <ToastContainer position="top-right" autoClose={4000} />
    </DashboardLayout>
  );
}
